/**
 * Правила личной колонки. Работают отдельно от распознавания вопросов:
 * сообщение, подошедшее под вкладку «Клиенты», «Сотрудники» или «Все», создаёт карточку
 * в колонке, даже если система не считает его вопросом.
 *  - delaySec — сколько сматченное сообщение «висит», прежде чем стать карточкой;
 *    если за это время его закрыли словом сотрудника — карточки не будет;
 *  - close — как закрывается карточка: только вручную или словом сотрудника (словарь или паттерн) после сматченного сообщения;
 *    close.employeeIds — чьё слово закрывает (пусто — любого сотрудника). Нужно там, где все в чате — сотрудники:
 *    в курьерских чатах «да» курьера не должно закрывать карточку, которую ждут от учёта.
 *  - trigger — чьё совпадение создаёт карточку: только клиента, только сотрудника или обеих сторон
 *    (двойное подтверждение: клиент и сотрудник в пределах double.windowSec; double.order — в любом порядке
 *    или только «сначала сотрудник» / «сначала клиент»: вторая сторона без первой ничего не начинает).
 *    «Все» засчитывается за сторону отправителя. Время карточки — время сообщения, на котором сработал
 *    триггер (для «оба» — сообщение, завершившее пару).
 *  - bots — сообщения ботов-сотрудников (kind = bot) участвуют как сообщения сотрудников: создают карточку и закрывают
 *    её словом (без флага колонка ботов не видит). Пример — отправки kesha0_bot: «/отпр» → бот пишет «🚀 … tronscan».
 *  - shareQuestion — сообщение клиента, пойманное колонкой, всё равно идёт и в системные колонки (не перехватывается):
 *    «Поставьте тез» ловит просьбы отправить тез, а вопрос о курсе в том же сообщении остаётся у трейдинга.
 *  - close.clientToo — слова закрытия засчитываются и от клиента (вопрос бота «полной отправлять?» → клиент «да»).
 *  - onlyPinned — колонка ловит только в чатах, закреплённых за ней (например, Форсаж — только курьерские чаты Форсажа);
 *    без флага личная колонка ловит во всех отслеживаемых чатах, кроме закреплённых за другими колонками.
 *  - onlyMe — со стороны сотрудников засчитываются только сообщения владельца колонки
 *    (сотрудник, к которому привязан его пользователь); остальные сотрудники колонку не трогают.
 *  - excludeColumns — системные колонки-исключения (выбираются в настройках колонки). Совпадение относится
 *    к последнему запросу в чате (последней карточке системных колонок после прошлого срабатывания колонки).
 *    Если это колонка-исключение — карточка не создаётся (outcome = 'excluded' в журнале).
 * Ответ сотрудника, «фикс сделки» и таймер «Брошен» карточки личных колонок не трогают.
 */
import { z } from 'zod';
import { findHit, findKeywordHit, validatePattern } from './patterns.js';

const pattern = z
  .string()
  .trim()
  .min(1)
  .superRefine((value, ctx) => {
    const err = validatePattern(value);
    if (err) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Невалидный паттерн: ${err}` });
  });

export const captureTabSchema = z.object({
  keywords: z.array(z.string().trim().min(1)).default([]),
  patterns: z.array(pattern).default([]),
});
export type CaptureTab = z.infer<typeof captureTabSchema>;

export const CAPTURE_TABS = ['client', 'staff', 'any'] as const;
export type CaptureTabKey = (typeof CAPTURE_TABS)[number];

export const CAPTURE_TRIGGERS = ['client', 'staff', 'both'] as const;

export const DOUBLE_ORDERS = ['any', 'staff_first', 'client_first'] as const;
export type DoubleOrder = (typeof DOUBLE_ORDERS)[number];
export type CaptureTrigger = (typeof CAPTURE_TRIGGERS)[number];

const captureObject = z.object({
  client: captureTabSchema.default({}),
  staff: captureTabSchema.default({}),
  any: captureTabSchema.default({}),
  delaySec: z.number().int().min(0).max(7 * 24 * 3600).default(0),
  trigger: z.enum(CAPTURE_TRIGGERS).default('client'),
  onlyMe: z.boolean().default(false),
  onlyPinned: z.boolean().default(false),
  bots: z.boolean().default(false),
  shareQuestion: z.boolean().default(false),
  excludeColumns: z.array(z.string().trim().min(1)).max(50).default([]),
  double: z
    .object({
      windowSec: z.number().int().min(60).max(24 * 3600).default(1800),
      order: z.enum(DOUBLE_ORDERS).default('any'),
    })
    .default({}),
  close: z
    .object({
      mode: z.enum(['manual', 'staff_words']).default('manual'),
      words: z.array(z.string().trim().min(1)).default([]),
      patterns: z.array(pattern).default([]),
      employeeIds: z.array(z.number().int().positive()).max(200).default([]),
      clientToo: z.boolean().default(false),
    })
    .default({}),
});

/** Старые колонки: вместо trigger был флаг double.enabled. */
function legacyTrigger(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || 'trigger' in raw) return raw;
  const r = raw as { double?: { enabled?: boolean }; client?: CaptureTab; staff?: CaptureTab; any?: CaptureTab };
  const rules = (t?: Partial<CaptureTab>) => (t?.keywords?.length ?? 0) + (t?.patterns?.length ?? 0);
  const trigger = r.double?.enabled ? 'both' : !rules(r.client) && !rules(r.any) && rules(r.staff) ? 'staff' : 'client';
  return { ...r, trigger };
}

export const captureSchema = z.preprocess(legacyTrigger, captureObject);
export type Capture = z.infer<typeof captureSchema>;

export const emptyCapture = (): Capture => captureSchema.parse({});

function matchTab(tab: CaptureTab, text: string): string | null {
  return findKeywordHit(tab.keywords, text)?.match ?? findHit(tab.patterns, text)?.match ?? null;
}

/** Подошло ли сообщение под колонку: сначала «Все», потом вкладка по отправителю. */
export function matchCapture(c: Capture, text: string | null, sender: 'client' | 'staff'): { tab: CaptureTabKey; hit: string } | null {
  if (!text?.trim() || !triggerAccepts(c, sender)) return null;
  const any = matchTab(c.any, text);
  if (any) return { tab: 'any', hit: any };
  const own = matchTab(c[sender], text);
  return own ? { tab: sender, hit: own } : null;
}

/** Может ли сообщение этой стороны участвовать в создании карточки. */
export const triggerAccepts = (c: Capture, sender: 'client' | 'staff') => c.trigger === 'both' || c.trigger === sender;

/** Слово сотрудника, закрывающее карточку колонки (режим staff_words). employeeId — кто написал (см. close.employeeIds). */
export function matchCloseWord(c: Capture, text: string | null, employeeId?: number): string | null {
  if (c.close.mode !== 'staff_words' || !text?.trim()) return null;
  // клиент (employeeId не задан) закрывает только при clientToo; список «кто закрывает» на клиента не распространяется
  if (employeeId == null) {
    if (!c.close.clientToo) return null;
    return findKeywordHit(c.close.words, text)?.match ?? findHit(c.close.patterns, text)?.match ?? null;
  }
  if (c.close.employeeIds.length && (employeeId == null || !c.close.employeeIds.includes(employeeId))) return null;
  return findKeywordHit(c.close.words, text)?.match ?? findHit(c.close.patterns, text)?.match ?? null;
}

/**
 * Может ли эта сторона начать пару двойного подтверждения. При «сначала сотрудник» сообщение клиента
 * без ожидающего сотрудника ничего не начинает (и наоборот).
 */
export const doubleCanStart = (c: Capture, sender: 'client' | 'staff') =>
  c.double.order === 'any' || c.double.order === (sender === 'staff' ? 'staff_first' : 'client_first');

/**
 * Колонка-исключение, из-за которой совпадение пропускается: все системные колонки, к которым относится
 * совпадение, входят в исключения. null — карточку создавать.
 */
export function excludedBy(c: Capture, activeColumns: readonly string[]): string | null {
  // activeColumns — колонки запроса, к которому относится совпадение
  if (!c.excludeColumns.length || !activeColumns.length) return null;
  return activeColumns.every((k) => c.excludeColumns.includes(k)) ? activeColumns[0]! : null;
}

export const DOUBLE_WINDOWS_SEC = [300, 900, 1800, 3600, 10800] as const;

export const hasCaptureRules = (c: Capture) => CAPTURE_TABS.some((k) => c[k].keywords.length || c[k].patterns.length);
